import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'src/store';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Loader from 'src/shared/components/loader/Loader';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import Show from 'src/shared/components/show/Show';
import usePreviewRelease from 'src/shared/hooks/usePreviewRelease';
import HabitatBaselinePicker from 'src/shared/components/habitat-baseline-picker/HabitatBaselinePicker';
import { baselineYearFor, baselineYearsBefore, habitatYears } from 'src/shared/helpers/habitatComparison';
import VegetationHealthChart from './components/VegetationHealthChart';
import ConnectivityChart from './components/ConnectivityChart';
import BiodiversityIntactnessChart from './components/BiodiversityIntactnessChart';
import HumanModificationChart from './components/HumanModificationChart';
import LandCoverChart from './components/LandCoverChart';
import MyPortfolioSection from './components/MyPortfolioSection';
import InvasiveSpeciesChart from './components/InvasiveSpeciesChart';
import IucnSummaryCard from './components/IucnSummaryCard';
import PortfolioActiveFilters from './components/PortfolioActiveFilters/PortfolioActiveFilters';
import usePortfolioAnalytics from './hooks/usePortfolioAnalytics';
import { usePortfolioOverviewQuery, useHabitatIndicatorsQuery } from '../../state/api/portfolioGraphSlice';
import { useSpeciesSummaryCards } from '../../hooks/useSpeciesSummaryCards';
import { PortfolioData } from '../../state/api/portfolioQuery';
import PortfolioFilters from '../../components/portfolio-filters/PortfolioFilters';

const PortfolioSummary = () => {
    const { currentCustomerId } = useCustomer();
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);

    const queryArgs = {
        customerId: currentCustomerId,
        ...(selectedFilters.length ? { filters: selectedFilters } : {}),
    };

    const { currentData: overview, isFetching: overviewFetching } = usePortfolioOverviewQuery(queryArgs);
    const { currentData: habitat, isFetching: habitatFetching } = useHabitatIndicatorsQuery(queryArgs);
    // Source-aware: the grid-backed page mounts this same component inside a provider.
    const { data: speciesCards, isFetching: speciesCardsFetching } = useSpeciesSummaryCards(queryArgs);

    const isFetching = overviewFetching || habitatFetching || speciesCardsFetching;

    const trackAnalytics = usePortfolioAnalytics();
    const [chosenBaseline, setChosenBaseline] = useState<number>();
    const years = habitatYears(habitat?.metrics ?? []);
    const showHabitatComparison = usePreviewRelease('habitatComparison');
    const latestYear = years[years.length - 1];
    const baselineYear = baselineYearFor(years, latestYear, chosenBaseline);

    // Assemble a PortfolioData-compatible object from the three purpose-built endpoints,
    // so that existing child components continue to work without prop changes.
    // Species come from the engine and can fail on their own; the habitat cards do not
    // depend on them, so a species failure leaves the rest of the page standing.
    const portfolioData: PortfolioData | null = useMemo(() => {
        if (!overview || !habitat) return null;
        return {
            objective: overview.objective ?? '',
            summary: {
                numberSites: overview.numberSites,
                totalArea: overview.totalArea,
                ownerCount: overview.ownerCount,
                countryCount: overview.countryCount,
                metrics: habitat.metrics,
                biiSiteSummary: habitat.biiSiteSummary,
                hmiSiteSummary: habitat.hmiSiteSummary,
                predictedSpeciesSummary: speciesCards
                    ? {
                          totalCount: speciesCards.totalCount,
                          redListSpecies: speciesCards.redListSpecies,
                          invasiveSpecies: speciesCards.invasiveSpecies,
                      }
                    : undefined,
            } as PortfolioData['summary'],
            assets: { filters: { owners: [], countries: [], years: [], availableMetrics: [] } },
        };
    }, [overview, habitat, speciesCards]);

    let mainContent = <div data-testid='portfolio-summary-no-data'>No portfolio data available</div>;
    if (portfolioData) {
        mainContent = (
            <div className='flex flex-col gap-10 mb-10' data-testid='portfolio-summary-content'>
                {/* Active Filters Section */}
                <PortfolioActiveFilters />

                {/* My Portfolio Section */}
                <MyPortfolioSection portfolioData={portfolioData} />

                {/* Habitat health indicators Section */}
                <div className='flex flex-col gap-3' data-testid='portfolio-habitat-health-section'>
                    <div className='flex justify-between items-center'>
                        <div className='text-xl text-black' data-testid='portfolio-habitat-health-heading'>
                            Habitat health indicators
                        </div>
                        <Show when={showHabitatComparison && years.length > 0}>
                            <HabitatBaselinePicker
                                baselineYear={baselineYear}
                                options={baselineYearsBefore(years, latestYear)}
                                onChange={year => {
                                    setChosenBaseline(year);
                                    trackAnalytics.trackHabitatBaselineChange(String(year));
                                }}
                            />
                        </Show>
                    </div>

                    <LandCoverChart portfolioData={portfolioData} chosenBaseline={chosenBaseline} />

                    <div className='flex space-between items-center gap-3'>
                        <VegetationHealthChart portfolioData={portfolioData} baselineYear={chosenBaseline} />
                        <ConnectivityChart portfolioData={portfolioData} baselineYear={chosenBaseline} />
                    </div>

                    <div className='flex space-between items-center gap-3'>
                        <BiodiversityIntactnessChart portfolioData={portfolioData} />
                        <HumanModificationChart portfolioData={portfolioData} />
                    </div>
                </div>

                {/* Species risk indicators Section */}
                <div className='flex flex-col gap-3' data-testid='portfolio-species-risk-section'>
                    <div className='text-xl text-black' data-testid='portfolio-species-risk-heading'>
                        Species risk indicators
                    </div>

                    <Show
                        when={portfolioData.summary.predictedSpeciesSummary !== undefined}
                        fallback={
                            <div
                                className='flex flex-col gap-2 p-5 rounded-lg bg-white shadow-portfolio-summary-card'
                                data-testid='portfolio-species-unavailable'
                            >
                                <h3 className='text-lg font-semibold text-primary'>Species predictions are not available right now</h3>
                                <p className='text-sm text-grey-70'>
                                    This is a temporary problem. Try again later. If the problem continues, contact your account manager.
                                </p>
                            </div>
                        }
                    >
                        <div className='flex space-between items-center gap-3'>
                            <div className='w-[395px]'>
                                <InvasiveSpeciesChart portfolioData={portfolioData} />
                            </div>
                            <IucnSummaryCard portfolioData={portfolioData} />
                        </div>
                    </Show>
                </div>
            </div>
        );
    }

    return (
        <StandardAppLayout
            title='Portfolio Summary'
            subTitle='Trends and indicators across your portfolio of sites'
            mainContent={
                <Show when={isFetching} fallback={mainContent}>
                    <Loader />
                </Show>
            }
            lozenges={<PortfolioFilters />}
            classNames={{
                otherActions: 'p-0 pt-1 justify-normal',
            }}
        ></StandardAppLayout>
    );
};

export default PortfolioSummary;
